import { COMISION_PORCENTAJE, DESCUENTO_DEL_REFERIDO_PORCENTAJE, DIAS_DE_MADURACION, MINIMO_DE_PAGO_CENTIMOS, RE_CORREO } from '@mc/socios'
import { BookUser, HandCoins, Link2, MousePointerClick, Repeat, Send, UserCheck, Wallet } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useOutletContext, useSearchParams } from 'react-router'

import { socio as accionesDeSocio, useRefrescarSocio, useSocio } from '../api/consultas'
import { ErrorDeLaApi } from '../api/nucleo'
import type { EstadoDePagoASocio, EstadoDeReferido, ResumenDeSocio } from '../api/tipos'
import { Escribenos } from '../componentes/Ayuda'
import { Aviso, Boton, Campo, clases, Esqueleto, EstadoVacio, Modal, Pastilla, Pestanas, Tarjeta, Titulo } from '../componentes/ui'
import { CajaDeEnlace } from '../socios/CajaDeEnlace'
import { dolares } from '../socios/dinero'
import { FichaEnElDirectorio, FormularioDeFicha } from '../socios/FichaDeAgencia'

import type { Contexto } from './Modulo'
type Pestana = 'agencias' | 'ganancias' | 'recomienda'

const PESTANAS: Array<{ clave: Pestana; etiqueta: string }> = [
  { clave: 'recomienda', etiqueta: 'Recomienda y gana' },
  { clave: 'ganancias', etiqueta: 'Ganancias' },
  { clave: 'agencias', etiqueta: 'Programa de agencias' },
]

const esPestana = (valor: null | string): valor is Pestana => valor === 'recomienda' || valor === 'ganancias' || valor === 'agencias'

const fecha = (iso: null | string | undefined) => (iso ? new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '—')

const mensajeDe = (fallo: unknown, siNo: string) => (fallo instanceof ErrorDeLaApi ? fallo.message : siNo)

const Cifra = ({ etiqueta, valor, nota, destacada = false }: { destacada?: boolean; etiqueta: string; nota?: ReactNode; valor: ReactNode }) => (
  <Tarjeta>
    <p className="text-[13px] text-tenue">{etiqueta}</p>
    <p className={clases('mt-1 font-display text-[28px] leading-none font-semibold tracking-tight tabular-nums', destacada && 'text-violeta')}>{valor}</p>
    {nota && <p className="mt-2 text-xs text-tenue">{nota}</p>}
  </Tarjeta>
)

const Paso = ({ numero, titulo, texto, icono }: { icono: ReactNode; numero: number; texto: string; titulo: string }) => (
  <div className="flex gap-3">
    <span aria-hidden className="flex size-9 shrink-0 items-center justify-center rounded-full bg-violeta-suave text-violeta [&>svg]:size-4">
      {icono}
    </span>
    <div>
      <p className="text-sm font-medium">
        <span className="text-tenue">{numero}.</span> {titulo}
      </p>
      <p className="mt-0.5 text-[13px] text-tenue">{texto}</p>
    </div>
  </div>
)

/**
 * «Recomienda y gana»: el programa de socios (CLAUDE.md §30). Todo cliente es socio
 * sin alta; aquí ve su enlace, lo que ha traído, lo que ha ganado, y pide el pago.
 * Las reglas —el 20 %, los 45 días, los $50— salen de `@mc/socios`, las mismas que
 * usa el núcleo para calcular: el panel no promete nada que el libro no cumpla.
 */
export const Socios = () => {
  const { espacio, permisos } = useOutletContext<Contexto>()
  // El programa de socios es del dueño de la cuenta (§30): no es una capacidad que se conceda.
  const administra = permisos.esDueno
  const { data, isPending, error } = useSocio(espacio, administra)
  const [params, setParams] = useSearchParams()
  const pestanaEnLaRuta = params.get('pestana')
  const pestana: Pestana = esPestana(pestanaEnLaRuta) ? pestanaEnLaRuta : 'recomienda'
  const cambiarPestana = (clave: string) => {
    const siguientes = new URLSearchParams(params)
    if (clave === 'recomienda') siguientes.delete('pestana')
    else siguientes.set('pestana', clave)
    setParams(siguientes, { replace: true })
  }

  if (!administra) {
    if (permisos.cargando) return <Esqueleto />
    return (
      <>
        <Titulo>Recomienda y gana</Titulo>
        <Aviso tono="info">Esto lo ve el propietario de la cuenta.</Aviso>
      </>
    )
  }
  if (error) return <Aviso tono="error">{mensajeDe(error, 'No hemos podido leer tu programa de socio. Vuelve a intentarlo en un momento.')}</Aviso>

  return (
    <>
      <Titulo descripcion="Tu enlace, lo que has traído y lo que has ganado. Cada cliente que recomiendas te paga una comisión, y él ahorra en su primer pago.">Recomienda y gana</Titulo>

      <div className="mb-6">
        <Pestanas activa={pestana} alCambiar={cambiarPestana} pestanas={PESTANAS} />
      </div>

      {isPending || !data ? (
        <Tarjeta sinRelleno>
          <Esqueleto filas={7} />
        </Tarjeta>
      ) : (
        <>
          {data.socio.estado === 'pausado' && (
            <div className="mb-6">
              <Aviso tono="aviso">
                Tu programa de socio está pausado. <Escribenos contexto="socio-pausado">Escríbenos</Escribenos> si crees que es un error.
              </Aviso>
            </div>
          )}
          {pestana === 'recomienda' && <Recomienda datos={data} />}
          {pestana === 'ganancias' && <Ganancias datos={data} espacio={espacio} />}
          {pestana === 'agencias' && <Agencias datos={data} espacio={espacio} />}
        </>
      )}
    </>
  )
}

// --- Recomienda y gana ------------------------------------------------------------------

const porcentajeDe = (parte: number, total: number) => (total > 0 ? `${Math.round((parte / total) * 100)} %` : '0 %')

const Recomienda = ({ datos }: { datos: ResumenDeSocio }) => {
  const { programa, socio, saldo, clics } = datos
  const comision = programa.comisionPorcentaje ?? COMISION_PORCENTAJE
  const descuento = programa.descuentoDelReferido ?? DESCUENTO_DEL_REFERIDO_PORCENTAJE
  const ganado = saldo.pagado + saldo.disponible + saldo.pendiente + saldo.reservado
  return (
    <div className="flex flex-col gap-6">
      {datos.vengoRecomendado && (
        <Aviso tono="info">
          Vienes recomendado por un socio de Wixy: tu primer pago lleva un {datos.vengoRecomendado.descuento} % de descuento, que aplicamos al facturar.
        </Aviso>
      )}

      <Tarjeta>
        <h2 className="font-display text-[22px] leading-tight font-semibold tracking-tight">Invita a otros y gana el {comision} %</h2>
        <p className="mt-1.5 mb-5 max-w-2xl text-sm text-tenue">
          Ellos ahorran el {descuento} % en su primer pago; tú ganas el {comision} % de {socio.recurrente ? 'cada pago' : 'ese primer pago'}. Sin tope de invitados.
        </p>
        <CajaDeEnlace codigo={socio.codigo} enlace={datos.enlace} />
      </Tarjeta>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Cifra etiqueta="Clics en tu enlace" nota={`${clics.ultimos30.toLocaleString('es')} en los últimos 30 días`} valor={clics.total.toLocaleString('es')} />
        <Cifra etiqueta="Cuentas creadas" nota={`${porcentajeDe(datos.cuentas, clics.total)} de los clics`} valor={datos.cuentas.toLocaleString('es')} />
        <Cifra etiqueta="Han pagado" nota={`${porcentajeDe(datos.pagaron, datos.cuentas)} de las cuentas`} valor={datos.pagaron.toLocaleString('es')} />
        <Cifra destacada etiqueta="Comisión ganada" nota={`${dolares(saldo.pagado)} ya cobrados`} valor={dolares(ganado)} />
      </div>

      <Tarjeta>
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-display text-base font-semibold">Clics en los últimos 30 días</h2>
          <p className="text-xs text-tenue">Cada navegador cuenta una vez por código; los robots, no.</p>
        </div>
        <GraficoDeClics porDia={clics.porDia} />
      </Tarjeta>

      <Tarjeta>
        <h2 className="mb-4 font-display text-base font-semibold">Cómo funciona</h2>
        <div className="grid gap-5 md:grid-cols-3">
          <Paso icono={<Send />} numero={1} texto="Por correo, por WhatsApp, en tu web: donde tengas a quien le vendría bien una web." titulo="Envías tu enlace" />
          <Paso icono={<HandCoins />} numero={2} texto={`Tu comisión queda pendiente ${DIAS_DE_MADURACION} días, el plazo en que un pago se puede devolver.`} titulo="Contratan y pagan" />
          <Paso icono={<Wallet />} numero={3} texto={`Desde ${dolares(MINIMO_DE_PAGO_CENTIMOS)} disponibles, a tu PayPal. Lo pides tú y lo envía una persona.`} titulo="Te pagamos" />
        </div>
      </Tarjeta>
    </div>
  )
}

/** Los treinta últimos días, en UTC y con la forma `AAAA-MM-DD` que usa el núcleo. */
const ultimosDias = (cuantos: number): string[] => {
  const hoy = Date.now()
  return Array.from({ length: cuantos }, (_, i) => new Date(hoy - (cuantos - 1 - i) * 86_400_000).toISOString().slice(0, 10))
}

const GraficoDeClics = ({ porDia }: { porDia: Array<{ dia: string; clics: number }> }) => {
  const dias = ultimosDias(30)
  const porFecha = new Map(porDia.map((d) => [d.dia, d.clics]))
  const serie = dias.map((dia) => ({ dia, clics: porFecha.get(dia) ?? 0 }))
  const maximo = Math.max(0, ...serie.map((d) => d.clics))
  if (maximo === 0) {
    return (
      <EstadoVacio
        descripcion="Cuando alguien abra tu enlace aparecerá aquí, día a día. Empieza por compartirlo con quien pueda necesitar una web."
        icono={<MousePointerClick />}
        titulo="Todavía nadie ha abierto tu enlace"
      />
    )
  }
  const etiqueta = (dia: string) => new Date(`${dia}T00:00:00Z`).toLocaleDateString('es', { day: 'numeric', month: 'short', timeZone: 'UTC' })
  return (
    <div>
      <div aria-label="Clics por día" className="flex h-32 items-end gap-[3px]" role="img">
        {serie.map((d) => (
          <div className="group relative flex h-full flex-1 items-end" key={d.dia} title={`${etiqueta(d.dia)}: ${d.clics.toLocaleString('es')} ${d.clics === 1 ? 'clic' : 'clics'}`}>
            <div
              className={clases('w-full rounded-t-[3px] transition-colors', d.clics > 0 ? 'bg-violeta group-hover:bg-violeta-2' : 'bg-papel-3')}
              style={{ height: d.clics > 0 ? `${Math.max(6, (d.clics / maximo) * 100)}%` : '3px' }}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[11px] text-tenue">
        <span>{etiqueta(serie[0]!.dia)}</span>
        <span>Hoy</span>
      </div>
    </div>
  )
}

// --- Ganancias --------------------------------------------------------------------------

const REFERIDO: Record<EstadoDeReferido, { texto: string; clave: 'construyendo' | 'neutra' | 'publicado' | 'violeta' }> = {
  registrado: { texto: 'Registrado', clave: 'neutra' },
  pendiente: { texto: 'Comisión pendiente', clave: 'construyendo' },
  aprobado: { texto: 'Comisión disponible', clave: 'publicado' },
  pagado: { texto: 'Cobrado', clave: 'violeta' },
}

const PAGO: Record<EstadoDePagoASocio, { texto: string; clave: 'construyendo' | 'fallida' | 'publicado' | 'violeta' }> = {
  solicitado: { texto: 'En revisión', clave: 'construyendo' },
  aprobado: { texto: 'Aprobado, en camino', clave: 'violeta' },
  enviado: { texto: 'Enviado por PayPal', clave: 'violeta' },
  fallido: { texto: 'PayPal no lo entregó', clave: 'fallida' },
  pagado: { texto: 'Pagado', clave: 'publicado' },
  rechazado: { texto: 'Rechazado', clave: 'fallida' },
}

/** Qué hacer con un pago que no ha terminado, en una frase (§31: PayPal lo entrega, o no). */
const QUE_HACER: Partial<Record<EstadoDePagoASocio, string>> = {
  enviado: 'Te llega en unos minutos. Si no tienes cuenta de PayPal con ese correo, ábrela con él para recibirlo.',
  fallido: 'Comprueba que tu correo de PayPal es el bueno (puedes cambiarlo arriba). El equipo lo vuelve a enviar o te escribe.',
}

const PREGUNTAS: Array<{ pregunta: string; respuesta: string }> = [
  { pregunta: '¿Qué da comisión?', respuesta: `Todo lo que tu referido pague a Wixy: su plan, los sitios extra y los paquetes de créditos. La comisión es el ${COMISION_PORCENTAJE} % del importe sin impuestos.` },
  { pregunta: '¿Puedo referir a un cliente que ya existe?', respuesta: 'No. Solo cuentan las cuentas nuevas creadas con tu enlace, y cada cliente pertenece al primer socio que lo trajo.' },
  { pregunta: `¿Por qué tarda ${DIAS_DE_MADURACION} días en estar disponible?`, respuesta: 'Es el plazo en el que un pago se puede devolver o disputar. Pasado ese tiempo la comisión es firme y pasa a disponible.' },
  { pregunta: '¿Cuánto tengo que juntar para pedir un pago?', respuesta: `${dolares(MINIMO_DE_PAGO_CENTIMOS)} disponibles. Cada envío tiene un coste, y por debajo de eso no compensa a nadie.` },
  { pregunta: '¿Cómo me pagan?', respuesta: 'Por PayPal, al correo que pongas aquí. Desde 50 $ disponibles pides el pago y sale solo a tu PayPal; te llega en unos minutos. Los pagos muy grandes los revisa antes una persona de Wixy. Verás la referencia en la tabla de pagos.' },
  { pregunta: '¿Mis invitados tienen descuento?', respuesta: `Sí: el ${DESCUENTO_DEL_REFERIDO_PORCENTAJE} % en su primer pago. Se lo llevan quienes crean su cuenta con tu enlace.` },
  { pregunta: '¿Cómo se cuentan los clics?', respuesta: 'Cada navegador cuenta una vez por código, aunque abra el enlace diez veces. Los robots y las vistas previas de WhatsApp o Telegram no cuentan. Solo guardamos cuántos: nunca quién.' },
  { pregunta: '¿Cobro solo del primer pago?', respuesta: 'Sí: la recomendación paga el primer pago de cada cliente que traigas. Las agencias socias cobran de todos los pagos mientras el cliente siga.' },
]

const Ganancias = ({ datos, espacio }: { datos: ResumenDeSocio; espacio: number }) => {
  const { saldo, socio, programa, pagos, referidos } = datos
  const refrescar = useRefrescarSocio(espacio)
  const acciones = accionesDeSocio(espacio)
  const minimo = programa.minimoDePago ?? MINIMO_DE_PAGO_CENTIMOS
  const enMarcha = pagos.find((p) => p.estado === 'solicitado' || p.estado === 'aprobado' || p.estado === 'enviado' || p.estado === 'fallido') ?? null
  const llega = saldo.faltaParaPedir === 0 && saldo.disponible > 0
  const progreso = Math.min(100, Math.round((saldo.disponible / minimo) * 100))
  const pausado = socio.estado === 'pausado'

  const [cambiandoPaypal, setCambiandoPaypal] = useState(false)
  const [pidiendo, setPidiendo] = useState(false)
  const [mensaje, setMensaje] = useState<null | { tono: 'error' | 'ok'; texto: string }>(null)

  const pedirPago = async () => {
    setPidiendo(true)
    setMensaje(null)
    try {
      const respuesta = await acciones.pedirPago()
      const { estado, importe, paypalEmail } = respuesta.pago
      setMensaje({
        tono: 'ok',
        texto:
          estado === 'enviado'
            ? `Te hemos enviado ${dolares(importe)} a ${paypalEmail} por PayPal. Te llegará en unos minutos.`
            : estado === 'aprobado'
              ? `Pago de ${dolares(importe)} aprobado: sale a ${paypalEmail} en cuanto PayPal lo procese.`
              : `Pago de ${dolares(importe)} pedido. Por su importe, una persona de Wixy lo revisa antes de enviarlo a ${paypalEmail}.`,
      })
      refrescar()
    } catch (fallo) {
      setMensaje({ tono: 'error', texto: mensajeDe(fallo, 'No hemos podido pedir el pago. Vuelve a intentarlo en un momento.') })
    } finally {
      setPidiendo(false)
    }
  }

  const motivoDeNoPedir = pausado
    ? null
    : enMarcha?.estado === 'fallido'
      ? 'PayPal no pudo entregar tu último pago: corrige tu correo de PayPal si hace falta; el equipo lo vuelve a enviar.'
      : enMarcha
        ? 'Ya tienes un pago en marcha: cuando se resuelva podrás pedir otro.'
        : !llega
        ? `Te faltan ${dolares(saldo.faltaParaPedir)} para pedir el pago.`
        : !socio.paypalEmail
          ? 'Pon tu correo de PayPal para poder pedirlo.'
          : null

  return (
    <div className="flex flex-col gap-6">
      {mensaje && <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso>}

      <Tarjeta className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] text-tenue">Disponible</p>
          <p className="mt-1 font-display text-[40px] leading-none font-semibold tracking-tight text-violeta tabular-nums">{dolares(saldo.disponible)}</p>
          <div className="mt-4 max-w-md">
            <div aria-hidden className="h-2 overflow-hidden rounded-full bg-papel-3">
              <div className={clases('h-full rounded-full transition-[width]', llega ? 'bg-ok' : 'bg-violeta')} style={{ width: `${progreso}%` }} />
            </div>
            <p className="mt-2 text-[13px] text-tenue">
              {enMarcha ? (enMarcha.estado === 'fallido' ? 'Tu último pago no llegó a tu PayPal: revisa el correo de PayPal.' : 'Tienes un pago en camino.') : llega ? 'Ya puedes pedir el pago.' : `Te faltan ${dolares(saldo.faltaParaPedir)} para pedir el pago.`} El mínimo es {dolares(minimo)}.
            </p>
          </div>
          {saldo.reservado > 0 && <p className="mt-2 text-[13px] text-tenue">{dolares(saldo.reservado)} están dentro de un pago ya pedido.</p>}
        </div>

        <div className="flex w-full flex-col gap-3 lg:w-80">
          <div className="rounded-caja border border-linea bg-papel-2 p-3.5">
            <p className="text-xs text-tenue">Método de pago</p>
            <div className="mt-1 flex items-center justify-between gap-3">
              <p className="min-w-0 truncate text-sm font-medium">{socio.paypalEmail ? `PayPal · ${socio.paypalEmail}` : 'Sin correo de PayPal'}</p>
              <Boton onClick={() => setCambiandoPaypal(true)} pequeno>
                {socio.paypalEmail ? 'Cambiar' : 'Añadir'}
              </Boton>
            </div>
          </div>
          {!pausado && (
            <>
              <Boton cargando={pidiendo} disabled={Boolean(motivoDeNoPedir)} icono={<HandCoins />} onClick={pedirPago} tipo="primario">
                Pedir el pago
              </Boton>
              {motivoDeNoPedir && <p className="text-xs text-tenue">{motivoDeNoPedir}</p>}
            </>
          )}
        </div>
      </Tarjeta>

      <div className="grid gap-4 sm:grid-cols-2">
        <Cifra etiqueta="Pendiente de madurar" nota={`Cada comisión pasa a disponible a los ${programa.diasDeMaduracion ?? DIAS_DE_MADURACION} días del pago.`} valor={dolares(saldo.pendiente)} />
        <Cifra etiqueta="Total pagado" nota="Lo que ya te hemos enviado." valor={dolares(saldo.pagado)} />
      </div>

      <section>
        <h2 className="mb-3 font-display text-lg font-semibold">Recomendaciones</h2>
        <Tarjeta sinRelleno>
          {!referidos.length ? (
            <p className="p-6 text-center text-sm text-tenue">Todavía nadie ha creado su cuenta con tu enlace.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-linea bg-papel-2 text-left text-xs text-tenue">
                    <th className="px-4 py-2.5 font-medium">Invitado</th>
                    <th className="px-4 py-2.5 font-medium">Estado</th>
                    <th className="px-4 py-2.5 font-medium">Se registró</th>
                    <th className="px-4 py-2.5 text-right font-medium">Ganancias</th>
                  </tr>
                </thead>
                <tbody>
                  {referidos.map((r) => (
                    <tr className="border-b border-linea last:border-b-0" key={r.id}>
                      <td className="px-4 py-3 font-mono text-[13px]">{r.correo}</td>
                      <td className="px-4 py-3">
                        <Pastilla clave={REFERIDO[r.estado]?.clave ?? 'neutra'}>{REFERIDO[r.estado]?.texto ?? r.estado}</Pastilla>
                      </td>
                      <td className="px-4 py-3 text-tenue tabular-nums">{fecha(r.fecha)}</td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums">{dolares(r.ganado)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Tarjeta>
      </section>

      <section>
        <h2 className="mb-3 font-display text-lg font-semibold">Pagos</h2>
        <Tarjeta sinRelleno>
          {!pagos.length ? (
            <p className="p-6 text-center text-sm text-tenue">Todavía no has pedido ningún pago.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-linea bg-papel-2 text-left text-xs text-tenue">
                    <th className="px-4 py-2.5 font-medium">Pedido</th>
                    <th className="px-4 py-2.5 font-medium">Estado</th>
                    <th className="px-4 py-2.5 font-medium">A</th>
                    <th className="px-4 py-2.5 font-medium">Referencia</th>
                    <th className="px-4 py-2.5 text-right font-medium">Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {pagos.map((p) => (
                    <tr className="border-b border-linea last:border-b-0" key={p.id}>
                      <td className="px-4 py-3 text-tenue tabular-nums">{fecha(p.pedidoEn)}</td>
                      <td className="px-4 py-3">
                        <Pastilla clave={PAGO[p.estado]?.clave ?? 'neutra'}>{PAGO[p.estado]?.texto ?? p.estado}</Pastilla>
                        {(p.estado === 'rechazado' || p.estado === 'fallido') && p.motivo && <p className="mt-1 text-xs text-tenue">{p.motivo}</p>}
                        {QUE_HACER[p.estado] && <p className="mt-1 max-w-xs text-xs text-tenue">{QUE_HACER[p.estado]}</p>}
                        {p.estado === 'pagado' && p.pagadoEn && <p className="mt-1 text-xs text-tenue">El {fecha(p.pagadoEn)}</p>}
                      </td>
                      <td className="px-4 py-3 text-tinta-2">{p.paypalEmail}</td>
                      <td className="px-4 py-3 font-mono text-[13px] text-tinta-2">{p.referencia ?? '—'}</td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums">{dolares(p.importe)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Tarjeta>
      </section>

      <section>
        <h2 className="mb-3 font-display text-lg font-semibold">Preguntas frecuentes</h2>
        <Tarjeta sinRelleno>
          {PREGUNTAS.map((p) => (
            <details className="group border-b border-linea px-5 last:border-b-0" key={p.pregunta}>
              <summary className="cursor-pointer list-none py-3.5 text-sm font-medium marker:content-none [&::-webkit-details-marker]:hidden">
                <span className="mr-2 inline-block text-tenue transition-transform group-open:rotate-90">›</span>
                {p.pregunta}
              </summary>
              <p className="pb-4 pl-5 text-sm text-tinta-2">{p.respuesta}</p>
            </details>
          ))}
        </Tarjeta>
      </section>

      {cambiandoPaypal && (
        <ModalDePaypal
          actual={socio.paypalEmail}
          cerrar={() => setCambiandoPaypal(false)}
          guardar={async (correo) => {
            await acciones.guardarPaypal(correo)
            refrescar()
            setMensaje({ tono: 'ok', texto: `Tus pagos irán a ${correo}.` })
          }}
        />
      )}
    </div>
  )
}

const ModalDePaypal = ({ actual, cerrar, guardar }: { actual: null | string; cerrar: () => void; guardar: (correo: string) => Promise<void> }) => {
  const [correo, setCorreo] = useState(actual ?? '')
  const [error, setError] = useState<null | string>(null)
  const [guardando, setGuardando] = useState(false)
  const enviar = async () => {
    const limpio = correo.trim().toLowerCase()
    if (!RE_CORREO.test(limpio)) {
      setError('Escribe el correo de tu cuenta de PayPal.')
      return
    }
    setGuardando(true)
    setError(null)
    try {
      await guardar(limpio)
      cerrar()
    } catch (fallo) {
      setError(mensajeDe(fallo, 'No hemos podido guardar el correo. Vuelve a intentarlo en un momento.'))
    } finally {
      setGuardando(false)
    }
  }
  return (
    <Modal
      ancho="max-w-md"
      cerrar={cerrar}
      pie={
        <>
          <Boton onClick={cerrar}>Cancelar</Boton>
          <Boton cargando={guardando} onClick={enviar} tipo="primario">
            Guardar
          </Boton>
        </>
      }
      titulo="Tu correo de PayPal"
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          void enviar()
        }}
      >
        <Campo
          autoComplete="email"
          autoFocus
          ayuda="Es a donde te enviaremos cada pago. Tiene que ser el correo de una cuenta de PayPal que pueda recibir dinero."
          error={error ?? undefined}
          etiqueta="Correo de PayPal"
          inputMode="email"
          onChange={(e) => setCorreo(e.target.value)}
          placeholder="tu@correo.com"
          type="email"
          value={correo}
        />
      </form>
    </Modal>
  )
}

// --- Programa de agencias -----------------------------------------------------------------

const Beneficio = ({ icono, titulo, texto }: { icono: ReactNode; texto: string; titulo: string }) => (
  <div className="rounded-caja border border-linea bg-papel-2 p-4">
    <span aria-hidden className="mb-3 flex size-9 items-center justify-center rounded-full bg-violeta-suave text-violeta [&>svg]:size-4">
      {icono}
    </span>
    <p className="text-sm font-medium">{titulo}</p>
    <p className="mt-1 text-[13px] text-tenue">{texto}</p>
  </div>
)

/**
 * La pestaña del programa de agencias, según en qué punto esté la agencia: sin pedir
 * (o rechazada) ve qué gana y la ficha para pedirlo; en revisión, su ficha, que puede
 * corregir; aprobada, cómo sale en el directorio y la ficha para editarla. El
 * formulario lleva el estado como `key`: al cambiar de estado vuelve a leer del núcleo.
 */
const Agencias = ({ datos, espacio }: { datos: ResumenDeSocio; espacio: number }) => {
  const agencia = datos.socio.agencia
  const estado = agencia?.estado ?? null
  const comision = datos.programa.comisionPorcentaje ?? COMISION_PORCENTAJE
  const pausado = datos.socio.estado === 'pausado'
  const motivo = agencia?.motivo?.trim()

  if (agencia && estado === 'aprobada') {
    return (
      <div className="flex flex-col gap-6">
        <Aviso tono="ok">
          Eres agencia socia desde el {fecha(agencia.resueltaEn ?? agencia.solicitadaEn)}: cobras el {comision} % de todos los pagos de tus referidos, mientras sigan en Wixy.
        </Aviso>
        <FichaEnElDirectorio agencia={agencia} pausado={pausado} />
        <FormularioDeFicha agencia={agencia} espacio={espacio} key="aprobada" pausado={pausado} />
      </div>
    )
  }

  if (agencia && estado === 'solicitada') {
    return (
      <div className="flex flex-col gap-6">
        <Aviso tono="info">
          Tu solicitud está en revisión desde el {fecha(agencia.solicitadaEn)}. Te contestamos por correo, normalmente en pocos días. Mientras tanto puedes corregir tu ficha.
        </Aviso>
        <FormularioDeFicha agencia={agencia} espacio={espacio} key="solicitada" pausado={pausado} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <Tarjeta className="md:p-6">
        <h2 className="font-display text-[22px] leading-tight font-semibold tracking-tight">Haz crecer tu agencia con Wixy como socia</h2>
        <p className="mt-1.5 mb-5 max-w-2xl text-sm text-tenue">
          Si montas webs para otros, cada cliente que traigas te paga mientras siga con nosotros, y tu agencia sale en el directorio donde otros buscan quién les haga la web.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Beneficio icono={<Repeat />} texto={`El ${comision} % de cada pago de tus referidos, no solo del primero.`} titulo="Comisiones recurrentes" />
          <Beneficio icono={<BookUser />} texto="Tu ficha en el directorio público de agencias de Wixy, con tu logo y lo que hacéis." titulo="Sal en el directorio" />
          <Beneficio icono={<Wallet />} texto={`Pides el pago desde ${dolares(MINIMO_DE_PAGO_CENTIMOS)} disponibles y una persona lo envía.`} titulo="Pagos por PayPal" />
          <Beneficio icono={<Link2 />} texto="Tu código y tu enlace no cambian: lo que ya compartiste sigue valiendo." titulo="El mismo enlace de siempre" />
        </div>
      </Tarjeta>

      <Tarjeta className="md:p-6">
        <h2 className="mb-4 font-display text-base font-semibold">Cómo funciona</h2>
        <div className="grid gap-5 md:grid-cols-3">
          <Paso icono={<Send />} numero={1} texto="Quiénes sois, cómo contactaros, qué hacéis y en qué idiomas. Con tu logo, si quieres." titulo="Rellenas tu ficha" />
          <Paso icono={<UserCheck />} numero={2} texto="Una persona de Wixy la revisa y te contesta por correo, normalmente en pocos días." titulo="La revisamos" />
          <Paso icono={<Repeat />} numero={3} texto={`Aprobada, cobras el ${comision} % de todos los pagos de tus referidos y sales en el directorio.`} titulo="Cobras de cada pago" />
        </div>
      </Tarjeta>

      {estado === 'rechazada' && (
        <Aviso tono="aviso">
          Tu solicitud anterior no se aprobó{motivo ? `: ${motivo}${/[.!?…]$/.test(motivo) ? '' : '.'}` : '.'} Corrige tu ficha y vuelve a pedirlo cuando quieras.
        </Aviso>
      )}
      <FormularioDeFicha agencia={agencia} espacio={espacio} key={estado ?? 'nueva'} pausado={pausado} />
    </div>
  )
}
